// src/components/ProtectedRoute.tsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useCurrentUser } from '../contexts/UserContext';
import { logRender } from '../utils/debugLoop';
import ConsentModal from './ConsentModal';

interface Props {
  children: React.ReactNode;
  /** When true, redirects to /sign-in with ?redirectTo= preserving current path */
  preserveRedirect?: boolean;
  /**
   * T5 (LAUNCH_PLAN.md): when true (default), users without DOB are redirected
   * to /complete-profile. Opt out (`requireOnboarded={false}`) only on routes
   * that themselves serve unonboarded users — the /complete-profile page itself.
   */
  requireOnboarded?: boolean;
  /**
   * T6 (LAUNCH_PLAN.md): when true (default), users without consent see a
   * blocking ConsentModal in place of children. Opt out only on routes whose
   * own form captures consent inline (the /complete-profile page).
   */
  requireConsent?: boolean;
}

export default function ProtectedRoute({
  children,
  preserveRedirect = false,
  requireOnboarded = true,
  requireConsent = true,
}: Props) {
  const { firebaseUser, currentUser, loading } = useCurrentUser();
  const location = useLocation();
  const hasProtectedSession = !!firebaseUser && !firebaseUser.isAnonymous;

  logRender('ProtectedRoute', undefined, { user: hasProtectedSession, loading });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-2xl">Loading...</div>
      </div>
    );
  }

  if (!hasProtectedSession) {
    if (preserveRedirect) {
      const fullPath = location.pathname + location.search + location.hash;
      const redirectTo = encodeURIComponent(fullPath);
      return <Navigate to={`/sign-in?redirectTo=${redirectTo}`} replace />;
    }
    return <Navigate to="/" replace />;
  }

  // T5: gate any signed-in-but-unonboarded user to /complete-profile.
  // Only checks when currentUser has resolved (loading guard above) and
  // when the caller hasn't explicitly opted out.
  if (
    requireOnboarded &&
    currentUser &&
    currentUser.hasOnboarded === false
  ) {
    return <Navigate to="/complete-profile" replace />;
  }

  // T6: gate any signed-in user without consent to the modal. Renders modal
  // in place of children — no app surface visible behind the backdrop.
  // Opt-out is only used by /complete-profile (which captures consent inline
  // alongside the onboarding form).
  if (
    requireConsent &&
    currentUser &&
    currentUser.hasConsent === false
  ) {
    return <ConsentModal />;
  }

  return <>{children}</>;
}
